/**
 * Sanitizes a payment amount string by removing unsupported characters.
 * Allows users to enter amounts in formats like: $1,454.69, 1,454.69, 1454.69
 * and returns a clean decimal number string.
 *
 * Commas are always treated as thousands separators and removed.
 * Periods are always treated as decimal separators.
 *
 * @param {string} value - The raw input value
 * @returns {string} - The sanitized value (numeric with optional decimal)
 */
export function sanitizePaymentAmount(value) {
    if (typeof value !== 'string') {
        value = String(value ?? '');
    }

    // Remove commas (thousands separators)
    value = value.replace(/,/g, '');

    // Remove currency symbols, spaces, and other non-numeric characters
    // Keep only digits and decimal point
    let sanitized = value.replace(/[^0-9.]/g, '');

    // Handle multiple decimal points - keep only the first one
    const parts = sanitized.split('.');
    if (parts.length > 2) {
        sanitized = parts[0] + '.' + parts.slice(1).join('');
    }

    // Remove leading zeros (except for values like "0.50")
    if (sanitized.length > 1 && sanitized.startsWith('0') && sanitized[1] !== '.') {
        sanitized = sanitized.replace(/^0+/, '');
        // Restore leading zero if result starts with decimal point (e.g. "00.50" → "0.50")
        if (sanitized.startsWith('.')) {
            sanitized = '0' + sanitized;
        }
    }

    // Ensure empty string returns empty (not "0" or "NaN")
    if (sanitized === '' || sanitized === '.') {
        return '';
    }

    return sanitized;
}

/**
 * Parses a payment amount string to a float, handling various input formats.
 *
 * @param {string} value - The raw input value
 * @returns {number} - The parsed float value, or NaN if invalid
 */
export function parsePaymentAmount(value) {
    const sanitized = sanitizePaymentAmount(value);
    return sanitized === '' ? NaN : parseFloat(sanitized);
}
